// ── M4: Profile Service ──────────────────────────────────────
// User profile CRUD, stats aggregation

import type { Profile, ProfileUpdate, Relationship, Task, PointsEntry } from '@/types'
import type { ProfileStats } from '@/types/profile'
import { supabase } from '@/lib/supabase'
import { isDemoMode_M12_4 } from '@/lib/constants'
import { profileUpdateSchema } from '@/lib/validation'
import { demoFetchProfile_M11_7 } from './demo'
import { isPreviewMode_M13_2, getPreviewProfile_M13_9, getPreviewProfileStats_M13_10 } from './preview'

/** M4_1: Fetch user profile by ID */
export async function fetchProfile_M4_1(userId: string): Promise<Profile | null> {
  if (isPreviewMode_M13_2()) {
    return getPreviewProfile_M13_9(userId)
  }
  if (isDemoMode_M12_4()) {
    return demoFetchProfile_M11_7(userId)
  }

  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .returns<Profile[]>()
    .single()

  if (error && error.code !== 'PGRST116') {
    throw new Error(`Failed to fetch profile: ${error.message}`)
  }
  return data
}

/** M4_2: Update profile fields */
export async function updateProfile_M4_2(
  userId: string,
  updates: ProfileUpdate
): Promise<Profile> {
  const validated = profileUpdateSchema.parse(updates)

  if (isDemoMode_M12_4()) {
    throw new Error('Profile updates not supported in demo mode')
  }

  const { data, error } = await supabase
    .from('profiles')
    .update({ ...validated, updated_at: new Date().toISOString() })
    .eq('id', userId)
    .select()
    .returns<Profile[]>()
    .single()

  if (error) throw new Error(`Failed to update profile: ${error.message}`)
  return data
}

/** M4_3: Calculate aggregated profile stats */
export async function fetchProfileStats_M4_3(userId: string): Promise<ProfileStats> {
  if (isPreviewMode_M13_2()) {
    return getPreviewProfileStats_M13_10()
  }
  if (isDemoMode_M12_4()) {
    return {
      totalPoints: 0,
      tasksCompleted: 0,
      tasksAssigned: 0,
      tasksVerified: 0,
      tasksRejected: 0,
      currentStreak: 0,
    }
  }

  // Fetch all tasks where user is involved via relationships
  const { data: relationships } = await supabase
    .from('relationships')
    .select('id')
    .or(`dom_id.eq.${userId},sub_id.eq.${userId}`)
    .eq('status', 'active')
    .returns<Pick<Relationship, 'id'>[]>()

  const relIds = (relationships ?? []).map((r) => r.id)

  if (relIds.length === 0) {
    return {
      totalPoints: 0,
      tasksCompleted: 0,
      tasksAssigned: 0,
      tasksVerified: 0,
      tasksRejected: 0,
      currentStreak: 0,
    }
  }

  const { data: tasks } = await supabase
    .from('tasks')
    .select('status')
    .in('relationship_id', relIds)
    .returns<Pick<Task, 'status'>[]>()

  const allTasks = tasks ?? []

  // Points total
  const { data: points } = await supabase
    .from('points_ledger')
    .select('points')
    .eq('user_id', userId)
    .returns<Pick<PointsEntry, 'points'>[]>()

  const totalPoints = (points ?? []).reduce((sum, p) => sum + p.points, 0)

  return {
    totalPoints,
    tasksCompleted: allTasks.filter((t) => t.status === 'completed').length,
    tasksAssigned: allTasks.filter((t) => t.status === 'pending').length,
    tasksVerified: allTasks.filter((t) => t.status === 'verified').length,
    tasksRejected: allTasks.filter((t) => t.status === 'rejected').length,
    currentStreak: 0, // TODO: calculate streak from consecutive verified days
  }
}
